'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { homeForRole, safeRedirectPath } from '@/lib/auth/utils';

interface FormData {
  email: string;
  password: string;
  fullName?: string;
  remember: boolean;
}

export default function ClientLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const { signIn, signUp, signOut, resetPassword, resolveRole } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';

  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm<FormData>({
    defaultValues: { email: prefillEmail, password: '', remember: false },
  });

  React.useEffect(() => {
    if (prefillEmail) {
      setValue('email', prefillEmail);
      setIsSignUp(true);
    }
  }, [prefillEmail, setValue]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      if (isSignUp) {
        await signUp(data.email, data.password, { fullName: data.fullName || '', role: 'client' });
        toast.success('Account created! Welcome to your Client Dashboard.');
        router.push(safeRedirectPath(searchParams.get('redirect'), '/client-dashboard'));
        return;
      }

      const result = await signIn(data.email, data.password);
      const role = await resolveRole(result.user);

      if (role && role !== 'client' && role !== 'admin') {
        await signOut();
        toast.error('This account is not a client account. Use the correct portal tab.');
        return;
      }

      toast.success('Welcome back! Redirecting to your Client Dashboard...');
      router.push(
        safeRedirectPath(searchParams.get('redirect'), homeForRole(role === 'admin' ? 'admin' : 'client'))
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Invalid credentials. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = getValues('email')?.trim();
    if (!email) {
      toast.error('Enter your email address first, then click Forgot password.');
      return;
    }
    setResetting(true);
    try {
      await resetPassword(email);
      toast.success('Password reset email sent. Check your inbox.');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Could not send reset email.';
      toast.error(message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-foreground">
          {isSignUp ? 'Create Client Account' : 'Client Portal Sign-In'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isSignUp ? 'Start planning your event today.' : 'Access your contracts, payments & event details.'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {isSignUp && (
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name</label>
            <input
              {...register('fullName')}
              className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Your full name"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
          <input
            {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/, message: 'Invalid email' } })}
            type="email"
            autoComplete="email"
            className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
            placeholder="your@email.com"
          />
          {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
          <div className="relative">
            <input
              {...register('password', { required: 'Password is required', minLength: isSignUp ? { value: 8, message: 'At least 8 characters' } : undefined })}
              type={showPassword ? 'text' : 'password'}
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              className="w-full px-3 py-2.5 pr-10 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
        </div>

        {!isSignUp && (
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input {...register('remember')} type="checkbox" className="w-4 h-4 rounded accent-primary" />
              <span className="text-xs text-muted-foreground">Remember me</span>
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetting}
              className="text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-60"
            >
              {resetting ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl text-sm font-bold gold-gradient text-black hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 size={15} className="animate-spin" /> {isSignUp ? 'Creating account...' : 'Signing in...'}</> : (isSignUp ? 'Create Account' : 'Sign In to Client Portal')}
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => setIsSignUp(!isSignUp)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
        </button>
      </div>
    </div>
  );
}
